import {
  ContextualAgentFlow,
  ProgramCheckInMethod,
  Programs,
} from "@/domain/programs.interface";
import { axios, transformAxiosError } from "@/utils/axios.create";

// ---------------------------------------------------------------------------
// Legacy API (DO NOT REMOVE) - consumed by other screens
// ---------------------------------------------------------------------------

const getPrograms = async ({
  sortType,
}: {
  sortType?: string;
}): Promise<Programs[]> => {
  return axios
    .get("/programs", { params: { status: "ACTIVE" } })
    .then((res) => {
      const body = res.data.body;

      const list: Programs[] = Array.isArray(body)
        ? (body as Programs[])
        : (body?.list as Programs[]) || [];
      return sortType === "desc" ? [...list].reverse() : list;
    })
    .catch((err) => {
      throw new Error(
        err.response?.data?.message || "Failed to fetch programs"
      );
    });
};

export const programService = {
  getPrograms,
};

// ---------------------------------------------------------------------------
// Full maintenance API
// ---------------------------------------------------------------------------

export type FindAllProgramsPayload = {
  search?: string;
  status?: string;
  orgUnitId?: string;
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

export type ProgramParametersPayload = {
  requiresCaseNumber?: boolean;
  maxParticipants?: number;
  budgetLimit?: number;
  maxEarningsPerParticipant?: number;
  startDate?: string | Date;
  endDate?: string | Date;
};

export type ProgramConfigurationPayload = {
  enabledJournalsModule?: boolean;
  enabledJournalsBackofficeView?: boolean;
  enabledServiceProvidersDirectoryModule?: boolean;
  enabledRewardUponAccountCreation?: boolean;
  enabledRewardUponCheckIn?: boolean;
  enabledRewardUponApptCreation?: boolean;
  enabledPushApptEveningBefore?: boolean;
  timePushApptEveningBefore?: number;
  enabledPushApptMorningBefore?: boolean;
  timePushApptMorningBefore?: number;
  enabledPushApptBefore?: boolean;
  minutesPushApptBefore?: number;
  enabledPushApptCheckInBefore?: boolean;
  minutesPushApptCheckInBefore?: number;
  enabledPushApptMissedFirst?: boolean;
  minutesPushApptMissedFirst?: number;
  enabledPushApptMissedSecond?: boolean;
  minutesPushApptMissedSecond?: number;
  enabledPushApptMissedThird?: boolean;
  minutesPushApptMissedThird?: number;
  enabledPushApptPostCheckInFirst?: boolean;
  minutesPushApptPostCheckInFirst?: number;
  enabledPushApptPostCheckInSecond?: boolean;
  minutesPushApptPostCheckInSecond?: number;
  enabledPushApptPostCheckInThird?: boolean;
  minutesPushApptPostCheckInThird?: number;
  enabledPushApptPostCheckInFourth?: boolean;
  minutesPushApptPostCheckInFourth?: number;
  enabledMissedAppointmentReason?: boolean;
  enabledRewardMissedApptReason?: boolean;
  enabledViewAppointmentMap?: boolean;
  onboardPhoneNumber?: string;
  participantUserGuideURL?: string;
  weeklyMissedRescheduleReminderEnabled?: boolean;
  postCheckInRescheduleReminderEnabled?: boolean;
  weeklyPostCheckinScheduleReminderEnabled?: boolean;
  enabledJournalsReward?: boolean;
  enabledContextualAgent?: boolean;
  enabledRewardCAFlow?: boolean;
  agentFlowNotOnWeekends?: boolean;
  contextualAgentPostFirstApptFollowUpEnabled?: boolean;
  contextualAgentHourPostFirstApptTrigger?: number;
  contextualAgentFlows?: ContextualAgentFlow[];
  viewBackofficeApptsEnabled?: boolean;
  potentialRewardsValue?: number;
  onboardReleasedDate?: string;
  programDays?: number;
  specifyAppointmentReason?: string;
  checkInMethod?: ProgramCheckInMethod;
};

export type ProgramPayload = {
  name: string;
  status: "PENDING" | "ACTIVE" | "COMPLETED" | "CANCELED";
  eventName?: string;
  orgUnitIds: string[];
  parameters?: ProgramParametersPayload;
  configuration?: ProgramConfigurationPayload;
  createdBy?: string;
  updatedBy?: string;
};

export type ProgramsListResponse = {
  list: Programs[];
  totalCount: number;
};

const findAll = async ({
  search,
  status,
  orgUnitId,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
}: FindAllProgramsPayload): Promise<ProgramsListResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  if (search) {
    queryParams.append("search", search);
  }

  if (status) {
    queryParams.append("status", status);
  }

  if (orgUnitId) {
    queryParams.append("orgUnitId", orgUnitId);
  }

  return axios
    .get(`/programs?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        list: body?.list || [],
        totalCount: body?.totalCount || 0,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findOne = async (id: string): Promise<Programs> => {
  return axios
    .get(`/programs/find/${id}`)
    .then((res) => {
      return res.data.body as Programs;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (data: ProgramPayload): Promise<Programs> => {
  return axios
    .post("/programs", data)
    .then((res) => {
      return res.data.body as Programs;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  data,
}: {
  id: string;
  data: ProgramPayload;
}): Promise<Programs> => {
  return axios
    .put(`/programs/${id}`, data)
    .then((res) => {
      return res.data.body as Programs;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const archive = async (id: string): Promise<void> => {
  return axios
    .patch(`/programs/archive/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const ProgramsService = {
  findAll,
  findOne,
  create,
  update,
  archive,
};
